import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { ArrowRight, Loader2 } from 'lucide-react';

import { cn } from '../../lib/utils';

/**
 * Circus Daily Button — Figma 8001:48767
 */
const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap font-sans text-cta-sm font-semibold ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        primary:
          'bg-primary text-primary-foreground hover:bg-brand-hover disabled:bg-grey-500 disabled:text-grey-300 disabled:opacity-100',
        default:
          'bg-primary text-primary-foreground hover:bg-brand-hover disabled:bg-grey-500 disabled:text-grey-300 disabled:opacity-100',
        secondary:
          'border border-primary bg-transparent text-white hover:bg-primary/10 disabled:border-grey-500 disabled:text-grey-400',
        outline:
          'border border-primary bg-transparent text-white hover:bg-primary/10 disabled:border-grey-500 disabled:text-grey-400',
        muted:
          'border border-border bg-surface-muted text-white hover:bg-border disabled:text-grey-400',
        soft: 'border-0 bg-secondary text-white hover:bg-secondary/80 disabled:text-grey-400',
        ghost: 'bg-transparent text-white hover:bg-surface-muted disabled:text-grey-400',
        link: 'bg-transparent text-white underline-offset-4 hover:underline disabled:text-grey-400 h-auto px-0',
        destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
      },
      shape: {
        rounded: 'rounded-[6px]',
        square: 'rounded-none',
        pill: 'rounded-full',
      },
      size: {
        default: 'h-[46px] px-4 py-2',
        sm: 'h-9 px-3',
        lg: 'h-12 px-8',
        icon: 'h-10 w-10 p-0',
        micro: 'h-auto px-3 py-2 text-sm',
      },
    },
    defaultVariants: {
      variant: 'primary',
      shape: 'rounded',
      size: 'default',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
  showTrailingIcon?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      shape,
      size,
      asChild = false,
      loading = false,
      showTrailingIcon = false,
      disabled,
      children,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : 'button';
    const isDisabled = disabled || loading;

    return (
      <Comp
        className={cn(buttonVariants({ variant, shape, size, className }))}
        ref={ref}
        disabled={isDisabled}
        {...props}
      >
        {loading ? (
          <Loader2 className="size-5 animate-spin" aria-hidden />
        ) : (
          <>
            {children}
            {showTrailingIcon && <ArrowRight className="size-4" aria-hidden />}
          </>
        )}
      </Comp>
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
